<template>
  <v-card
    @click="() => emit('click')"
    class="rounded-lg position-relative overflow-visible"
    :style="isOverdue ? overdueStyle : isComingDue ? comingDueStyle : undefined"
  >
    <div class="bg-white corner-warning-icon" v-if="isOverdue || isComingDue">
      <div
        class="corner-warning-icon-fill"
        :class="{ 'error-bg': isOverdue, 'warning-bg': isComingDue }"
      >
        <v-icon
          icon="mdi-alert-circle-outline"
          :color="isOverdue ? 'error' : 'warning'"
          size="25"
        />
      </div>
    </div>
    <v-card-title class="text-truncate text-subtitle-1 font-weight-medium">
      {{ vehicle.name ?? "unnamed vehicle" }}
    </v-card-title>
    <v-card-subtitle class="font-weight-medium">
      {{ vehicle.licensePlate ?? "unrecorded license plate" }}
    </v-card-subtitle>
    <v-card-text class="d-flex align-center">
      <v-icon color="primary" size="small" class="mr-1">
        {{ vehicle.fuelTypeIcon }}
      </v-icon>
      {{ vehicle.fuelTypeDisplayText }}

      <template v-if="makeModelYearText">
        <v-icon
          icon="mdi-information-outline"
          class="mr-1 ml-3"
          color="primary"
        />
        {{ makeModelYearText }}
      </template>
    </v-card-text>
  </v-card>
</template>

<script lang="ts" setup>
import { useInspectionHistoryStore } from "@/domains/inspectionHistory";
import {
  getVehicleComplianceFlags,
  useVehicleStore,
  type Vehicle,
} from "@/domains/vehicles";
import { computed } from "vue";

const { vehicle } = defineProps<{ vehicle: Vehicle }>();
const emit = defineEmits<{
  (e: "click"): void;
}>();

const vehicleStore = useVehicleStore();
const inspectionHistoryStore = useInspectionHistoryStore();

/**
 * "{make} {model} ({year})", derived from the vehicle's EPA fuel efficiency
 * rating. Undefined when no rating has been resolved/fetched for this
 * vehicle.
 */
const makeModelYearText = computed(() => {
  const rating = vehicle?.externalId
    ? vehicleStore.getEpaFuelEfficiencyRatingForVehicle(vehicle.externalId)
    : undefined;

  return rating
    ? `${rating.make} ${rating.model} (${rating.model_year})`
    : undefined;
});

// Deliberately only "overdue" (error) and "due soon" (warning) get a
// highlight here - there's no "success" style for compliant vehicles. A
// fully compliant fleet is the expected, unremarkable state, not something
// that needs a green card to celebrate; adding one turns this list into a
// traffic-light board, which is more visual noise than signal.
const complianceFlags = computed(() =>
  getVehicleComplianceFlags(
    vehicle,
    vehicle.externalId
      ? inspectionHistoryStore.getComplianceStatusForVehicle(
          vehicle.externalId,
        )
      : [],
  ),
);

const isOverdue = computed(() => complianceFlags.value.isOverdue);
const isComingDue = computed(() => complianceFlags.value.isComingDue);

// Vuetify stores each theme colour as a bare "r, g, b" CSS variable (e.g.
// --v-theme-error), specifically so it can be dropped into rgb()/rgba() -
// there's no separate "error20"-style fill token defined in vuetify.ts like
// there is for primary (primary20), so the 20%-alpha fill here is built by
// rgba()-wrapping the theme colour directly instead.
const overdueStyle = {
  border: "1px solid rgb(var(--v-theme-error))",
  background: "rgba(var(--v-theme-error), 0.2)",
};

const comingDueStyle = {
  border: "1px solid rgb(var(--v-theme-warning))",
  background: "rgba(var(--v-theme-warning), 0.2)",
};
</script>

<style scoped>
/*
 * Sits half outside the card (negative top/right) so it reads as a badge
 * pinned to the corner rather than inline content. pointer-events: none
 * keeps it purely decorative - clicks pass straight through to the card's
 * own click handler underneath.
 */
.corner-warning-icon {
  position: absolute;
  top: -6px;
  right: -6px;
  pointer-events: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}

/*
 * Same explicit box as .corner-warning-icon itself, layered directly on top
 * of it - not put on the v-icon element, whose own rendered box doesn't
 * line up pixel-for-pixel with an explicit width/height div and left a thin
 * rim of the white backdrop showing around the tinted circle.
 */
.corner-warning-icon-fill {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.error-bg {
  background: rgba(var(--v-theme-error), 0.2);
}

.warning-bg {
  background: rgba(var(--v-theme-warning), 0.2);
}
</style>
